import { useTranslation } from 'react-i18next';

export type EmporterKind = 'emporter' | 'livraison';

interface Props {
  emporterCount: number;
  deliveryCount: number;
  active: EmporterKind;
  onSelect: (kind: EmporterKind) => void;
  /** Rien sous le selecteur dans la meme bande collante (vue Livraison, ou
   *  Emporter vide) : il porte alors lui-meme le filet du bas. */
  standalone?: boolean;
}

const KINDS: EmporterKind[] = ['emporter', 'livraison'];

const LABEL_KEY: Record<EmporterKind, string> = {
  emporter: 'waiter.notes.kind.emporter',
  livraison: 'waiter.notes.kind.livraison',
};

/**
 * SC-1274 — en tete de l'onglet Emporter, seulement pour un restaurant qui
 * livre (`useWaiterDeliveryEnabled`) : « Emporter » (par defaut, le cas
 * courant) | « Livraison ». Pas un 4e onglet : la rangee d'onglets n'a plus
 * un pixel de libre a 360px (memoire `waiter_tab_row_has_no_room_for_another_icon`).
 * La pastille de « Livraison » est ce qui empeche une livraison de passer
 * inapercue pendant que le serveur regarde « Emporter ».
 */
export default function EmporterKindSwitch({
  emporterCount,
  deliveryCount,
  active,
  onSelect,
  standalone = false,
}: Props) {
  const { t } = useTranslation();
  const counts: Record<EmporterKind, number> = { emporter: emporterCount, livraison: deliveryCount };

  return (
    <div
      data-testid="emporter-kind-switch"
      className={`px-3 pt-2 bg-paper${standalone ? ' pb-2 border-b border-hairline' : ''}`}
    >
      <div
        role="group"
        aria-label={t('waiter.notes.kind.groupLabel')}
        className="flex gap-1 rounded-full bg-ash border border-hairline"
      >
        {KINDS.map((kind) => {
          const selected = active === kind;
          return (
            <button
              key={kind}
              type="button"
              data-testid={`emporter-kind-${kind}`}
              aria-pressed={selected}
              onClick={() => onSelect(kind)}
              className={`flex-1 min-w-0 h-11 px-1.5 flex items-center justify-center gap-1 rounded-full font-sans text-base ${
                selected ? 'bg-ink text-paper font-bold' : 'text-ink font-semibold'
              }`}
            >
              <span className="truncate">{t(LABEL_KEY[kind])}</span>
              {/* « Emporter » selectionne : « Tout n », juste dessous, dit
                  deja le meme nombre. */}
              {counts[kind] > 0 && !(kind === 'emporter' && selected) && (
                <span
                  data-testid={`emporter-kind-${kind}-count`}
                  className="shrink-0 min-w-[20px] h-[20px] px-1 rounded-full text-white text-[13px] font-bold flex items-center justify-center"
                  style={{ backgroundColor: 'var(--w-info)' }}
                >
                  {counts[kind]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
